/**
 * Match-level insight stats from completed match sets (no point-log required).
 * Powered by score aggregates already on MatchRecord — sport-agnostic.
 */

/**
 * @param {Array<{ scoreA?: number, scoreB?: number, winner?: string }>} sets
 */
export function computeSidePointTotals(sets) {
  let pointsA = 0;
  let pointsB = 0;
  let gamesA = 0;
  let gamesB = 0;
  (sets || []).forEach((s) => {
    const a = Number(s.scoreA) || 0;
    const b = Number(s.scoreB) || 0;
    pointsA += a;
    pointsB += b;
    if (s.winner === "A" || a > b) gamesA += 1;
    else if (s.winner === "B" || b > a) gamesB += 1;
  });
  return { pointsA, pointsB, gamesA, gamesB, totalPoints: pointsA + pointsB };
}

/**
 * Cumulative points after each game — for score progression chart.
 * @param {Array<{ scoreA?: number, scoreB?: number }>} sets
 * @returns {{ labels: string[], seriesA: number[], seriesB: number[] }}
 */
export function buildScoreProgression(sets) {
  const labels = [];
  const seriesA = [0];
  const seriesB = [0];
  let cumA = 0;
  let cumB = 0;
  (sets || []).forEach((s, i) => {
    cumA += Number(s.scoreA) || 0;
    cumB += Number(s.scoreB) || 0;
    labels.push(`G${i + 1}`);
    seriesA.push(cumA);
    seriesB.push(cumB);
  });
  return { labels, seriesA, seriesB };
}

/**
 * Per-game race sparkline endpoints (final scores only — honest, not reconstructed rallies).
 * @param {Array<{ scoreA?: number, scoreB?: number, winner?: string }>} sets
 */
export function buildGameScoreBars(sets) {
  return (sets || []).map((s, i) => {
    const a = Number(s.scoreA) || 0;
    const b = Number(s.scoreB) || 0;
    const max = Math.max(a, b, 1);
    return {
      index: i,
      label: `Game ${i + 1}`,
      scoreA: a,
      scoreB: b,
      pctA: Math.round((a / max) * 100),
      pctB: Math.round((b / max) * 100),
      winner: s.winner || (a > b ? "A" : b > a ? "B" : null),
      margin: Math.abs(a - b),
    };
  });
}

/**
 * Comparative cool stats for Match overview (Garmin / Chess.com energy).
 * @param {object} input
 * @param {Array<{ scoreA?: number, scoreB?: number, winner?: string }>} input.sets
 * @param {string} [input.winnerSide]
 * @param {number | null} [input.durationSec]
 * @param {string} [input.teamALabel]
 * @param {string} [input.teamBLabel]
 */
export function buildMatchInsightStats(input) {
  const sets = input.sets || [];
  const { pointsA, pointsB, gamesA, gamesB, totalPoints } = computeSidePointTotals(sets);
  const durationSec = input.durationSec ?? null;
  const games = buildGameScoreBars(sets);

  let closestMargin = null;
  let biggestMargin = null;
  games.forEach((g) => {
    if (closestMargin == null || g.margin < closestMargin) closestMargin = g.margin;
    if (biggestMargin == null || g.margin > biggestMargin) biggestMargin = g.margin;
  });

  const firstGame = games[0];
  const wonMatch = input.winnerSide === "A" || input.winnerSide === "B";
  const lostFirstWonMatch =
    Boolean(firstGame?.winner) &&
    wonMatch &&
    firstGame.winner !== input.winnerSide &&
    games.length > 1;

  const sweep =
    wonMatch &&
    games.length > 0 &&
    games.every((g) => g.winner === input.winnerSide);

  const pointsPerMin =
    durationSec && durationSec > 0 && totalPoints > 0
      ? Math.round((totalPoints / (durationSec / 60)) * 10) / 10
      : null;

  const dominanceA =
    totalPoints > 0 ? Math.round((pointsA / totalPoints) * 100) : null;
  const dominanceB =
    totalPoints > 0 ? Math.round((pointsB / totalPoints) * 100) : null;

  /** @type {Array<{ label: string, valueA: string | number, valueB: string | number }>} */
  const comparisonRows = [
    { label: "Match points", valueA: gamesA, valueB: gamesB },
    { label: "Points won", valueA: pointsA, valueB: pointsB },
    {
      label: "Point differential",
      valueA: pointsA - pointsB >= 0 ? `+${pointsA - pointsB}` : String(pointsA - pointsB),
      valueB: pointsB - pointsA >= 0 ? `+${pointsB - pointsA}` : String(pointsB - pointsA),
    },
    {
      label: "Share of points",
      valueA: dominanceA != null ? `${dominanceA}%` : "—",
      valueB: dominanceB != null ? `${dominanceB}%` : "—",
    },
  ];

  if (closestMargin != null) {
    comparisonRows.push({
      label: "Closest game margin",
      valueA: input.winnerSide === "A" ? closestMargin : "—",
      valueB: input.winnerSide === "B" ? closestMargin : "—",
    });
  }

  /** Highlight chips — only when true / computable */
  /** @type {Array<{ id: string, label: string, tone: string }>} */
  const highlights = [];
  if (sweep) highlights.push({ id: "sweep", label: "Straight games", tone: "win" });
  if (lostFirstWonMatch) {
    highlights.push({ id: "comeback", label: "Comeback win", tone: "amber" });
  }
  if (biggestMargin != null && biggestMargin >= 10) {
    highlights.push({ id: "blowout", label: `Biggest margin ${biggestMargin}`, tone: "purple" });
  }
  if (closestMargin === 0) {
    highlights.push({ id: "deuce", label: "Tied game score", tone: "muted" });
  } else if (closestMargin === 1 || closestMargin === 2) {
    highlights.push({ id: "tight", label: "Nail-biter game", tone: "amber" });
  }
  if (pointsPerMin != null && pointsPerMin >= 8) {
    highlights.push({ id: "pace", label: "High pace", tone: "purple" });
  }

  const quickStats = [
    { label: "Games", value: `${gamesA}–${gamesB}` },
    { label: "Points", value: `${pointsA}–${pointsB}` },
    {
      label: "Diff",
      value:
        pointsA === pointsB
          ? "0"
          : pointsA > pointsB
            ? `+${pointsA - pointsB}`
            : `−${pointsB - pointsA}`,
    },
  ];
  if (durationSec != null && durationSec > 0) {
    const mins = Math.max(1, Math.round(durationSec / 60));
    quickStats.push({ label: "Time", value: `${mins}m` });
  }
  if (pointsPerMin != null) {
    quickStats.push({ label: "Pts/min", value: String(pointsPerMin) });
  }

  return {
    teamALabel: input.teamALabel || "Team A",
    teamBLabel: input.teamBLabel || "Team B",
    pointsA,
    pointsB,
    gamesA,
    gamesB,
    totalPoints,
    closestMargin,
    biggestMargin,
    pointsPerMin,
    dominanceA,
    dominanceB,
    sweep,
    comeback: lostFirstWonMatch,
    comparisonRows,
    highlights,
    quickStats,
    games,
    progression: buildScoreProgression(sets),
    timeline: buildMatchEventTimeline({
      sets: games,
      winnerSide: input.winnerSide,
      durationSec,
      teamALabel: input.teamALabel,
      teamBLabel: input.teamBLabel,
    }),
  };
}

/**
 * Lightweight match event timeline (game outcomes + result).
 */
export function buildMatchEventTimeline(input) {
  const events = [];
  const games = input.sets || [];
  games.forEach((g) => {
    const winnerLabel =
      g.winner === "A"
        ? input.teamALabel || "Team A"
        : g.winner === "B"
          ? input.teamBLabel || "Team B"
          : "—";
    events.push({
      id: `game_${g.index}`,
      title: `${g.label} · ${g.scoreA}–${g.scoreB}`,
      detail: g.winner ? `Won by ${winnerLabel}` : "Completed",
      tone: g.winner === "A" ? "a" : g.winner === "B" ? "b" : "muted",
    });
  });
  if (input.winnerSide) {
    const champ =
      input.winnerSide === "A"
        ? input.teamALabel || "Team A"
        : input.teamBLabel || "Team B";
    events.push({
      id: "match_end",
      title: "Match complete",
      detail: `${champ} win`,
      tone: "win",
    });
  }
  if (input.durationSec != null && input.durationSec > 0) {
    const mins = Math.round(input.durationSec / 60);
    events.push({
      id: "duration",
      title: "Duration",
      detail: mins < 60 ? `${mins} minutes` : `${Math.floor(mins / 60)}h ${mins % 60}m`,
      tone: "muted",
    });
  }
  return events;
}

/**
 * Quick stats strip for recent-match cards from set columns / score summary.
 * @param {object} card match card view model
 */
export function buildCardQuickStats(card) {
  const sets =
    card?.setColumns?.length
      ? card.setColumns.map((col) => ({
          scoreA: card.myTeam === "B" ? col.opp : col.my,
          scoreB: card.myTeam === "B" ? col.my : col.opp,
          winner: col.iWonSet ? card.myTeam : card.myTeam === "A" ? "B" : "A",
        }))
      : [];
  if (!sets.length && card?.scoreSummary) {
    String(card.scoreSummary)
      .split(",")
      .forEach((part) => {
        const m = part.trim().match(/^(\d+)\s*[-–]\s*(\d+)$/);
        if (!m) return;
        const a = Number(m[1]);
        const b = Number(m[2]);
        sets.push({
          scoreA: a,
          scoreB: b,
          winner: a >= b ? "A" : "B",
        });
      });
  }
  if (!sets.length) {
    return [
      { label: "Result", value: card?.isWin ? "W" : "L" },
      ...(card?.durationLabel ? [{ label: "Time", value: card.durationLabel }] : []),
    ];
  }
  const insights = buildMatchInsightStats({
    sets,
    winnerSide: card?.isWin ? card.myTeam : card?.myTeam === "A" ? "B" : "A",
    durationSec: card?.durationSec ?? null,
  });
  return insights.quickStats.slice(0, 4);
}
